import { competitionRank } from './rank'

/*
 * The week, as a paragraph you can paste into the family thread.
 *
 * This is the half of the notifications answer that works today. Push may or
 * may not arrive; SMS from a Worker means carrier registration, an opt-out
 * obligation and a number nobody recognises. The commissioner pasting this into
 * the group chat that already exists costs nothing, needs no consent record,
 * and is informal for the simplest possible reason - it is genuinely him
 * sending it.
 *
 * TWO LISTS, THE WEEK FIRST AND THE SEASON UNDER IT.
 *
 * This was one line per player carrying both facts - "1. Jude 8 · 3rd, 15" -
 * to keep a sixteen-player recap short enough that iMessage would not collapse
 * it behind "Read More". It is shorter and it reads wrong: the two halves are
 * ranked on different things and sorted in a different order, so the line is a
 * week ranking with a season ranking stapled to it, and the reader has to
 * unpick which number belongs to which. Two plain lists say it once each.
 * Scott's call, 2026-09-13.
 *
 * If a roster this size does get collapsed, the fix is fewer lines per list,
 * not two facts per line.
 *
 * NOTHING LINES UP WITH SPACES. A message bubble renders proportionally, so
 * padded columns look ragged on every phone except the one they were tuned on.
 *
 * Ranks come from `competitionRank` - the app's own - because the version that
 * gets screenshotted and argued over is this one, and a recap that disagrees
 * with the screen is worse than no recap.
 */

export interface RecapPlayer {
  name: string
  weekPoints: number
  seasonPoints: number
}

/** The board that is up NOW, when it is a different one from the recap's. */
export interface RecapBoard {
  label: string
  games: number
  /** The deadline in words, or null when it cannot be named. */
  closes: string | null
}

export interface RecapInput {
  /** The commissioner's label for the week being recapped, e.g. "Week 2". */
  label: string
  /** Every game final. Decides "final" against "so far". */
  complete: boolean
  players: readonly RecapPlayer[]
  /** Whether each line carries its season half. */
  withSeason: boolean
  /**
   * How many weeks have been played at all.
   *
   * At one, a player's season points ARE their week points, so the second half
   * of every line would repeat the number three characters to its left. It is
   * suppressed regardless of `withSeason`.
   */
  weeksPlayed: number
  board: RecapBoard | null
  url: string
}

/** "T3" for a shared place, "3" for a clear one. */
function place(rank: number, shared: ReadonlySet<number>): string {
  return `${shared.has(rank) ? 'T' : ''}${rank}`
}

/** The places more than one player is standing on. */
function sharedPlaces(ranks: readonly number[]): Set<number> {
  return new Set(ranks.filter((r, i) => ranks.indexOf(r) !== i))
}

/**
 * One ranked list: "1. Jude 8", ties marked.
 *
 * Both lists are built by this, so the week and the season cannot end up
 * ordered, ranked or written two different ways - and the tie rule is stated
 * once. Alphabetical inside a tie, so the order is stable rather than however
 * the rows happened to arrive.
 */
function standings(
  players: readonly RecapPlayer[],
  points: (p: RecapPlayer) => number,
): string[] {
  const order = [...players].sort(
    (a, b) => points(b) - points(a) || a.name.localeCompare(b.name),
  )
  const ranks = competitionRank(order.map(points))
  const shared = sharedPlaces(ranks)
  return order.map((p, i) => `${place(ranks[i] ?? i + 1, shared)}. ${p.name} ${points(p)}`)
}

export function buildRecap({
  label,
  complete,
  players,
  withSeason,
  weeksPlayed,
  board,
  url,
}: RecapInput): string {
  const season = withSeason && weeksPlayed > 1 && players.length > 0

  const out: string[] = [`${label} ${complete ? 'final' : 'so far'}`, '']

  if (players.length === 0) {
    out.push('No scores yet.')
  } else {
    // A single list needs no heading to say which one it is.
    if (season) out.push('WEEK')
    out.push(...standings(players, (p) => p.weekPoints))

    /*
     * THE SEASON LIST IS SORTED AND RANKED ON ITS OWN, over the whole field.
     * Reusing the week's order and only changing the numbers would tell the
     * family that whoever won the week leads the season - wrong in most weeks,
     * and the single most embarrassing thing this message could say.
     */
    if (season) {
      out.push('', 'SEASON')
      out.push(...standings(players, (p) => p.seasonPoints))
    }
  }

  if (board) {
    out.push('', `${board.label} is up — ${board.games} ${board.games === 1 ? 'game' : 'games'}.`)
    // Only when the board can actually name it. An invented deadline in the
    // family thread is worse than none, because people will believe it.
    if (board.closes) out.push(`Picks close ${board.closes}.`)
  }

  out.push('', url)
  return out.join('\n')
}
